import type { CabinetAppointmentView } from "@planerius/contracts/cabinet"
import { useCallback, useMemo, useState } from "react"

import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import {
  markAppointmentArrived,
  markAppointmentCompleted,
  markAppointmentNoShow,
} from "../../api/journal-actions.api"
import { revealClientPhone } from "../../api/clients.api"
import { appointmentIntent, createIdempotencyBook } from "../../journal/idempotency-book"

/**
 * Отметки визита с телефона мастера: пришёл, завершено, не пришёл.
 *
 * ТРИ ПРАВИЛА, КОТОРЫЕ ЗДЕСЬ СОБЛЮДАЮТСЯ И ЛОМАЮТСЯ МОЛЧА, ЕСЛИ ИХ НЕ СОБЛЮДАТЬ.
 *
 * 1. КЛЮЧ ИДЕМПОТЕНТНОСТИ ЖИВЁТ У НАМЕРЕНИЯ. Мастер нажимает «Клиент пришёл» мокрой
 *    рукой в зале с плохой связью; ответ не доезжает, он жмёт ещё раз. Второй запрос
 *    обязан попасть в тот же ключ и вернуть тот же результат, а не вторую отметку.
 *    Книга ключей (journal/idempotency-book.ts) помнит намерение, а не нажатие.
 *
 * 2. ВЕРСИЯ ЗАПИСИ ОБЯЗАТЕЛЬНА. expectedUpdatedAt — это та версия, которую человек
 *    ВИДЕЛ. Пока карточка была открыта, администратор мог перенести запись; тогда сервер
 *    ответит version_conflict, и это не сбой, а ответ: действие применяется только
 *    к той версии, которую видели. Отметка «пришёл» на перенесённый визит — это чужой
 *    клиент, отмеченный пришедшим.
 *
 * 3. ОТКАЗ НЕ СТИРАЕТ ЭКРАН. День остаётся на месте, отказ показывается строкой рядом
 *    с кнопкой. Мастеру негде перезагружать страницу — у него в кресле человек.
 */

export type VisitActionKind = "arrived" | "completed" | "no-show"

export interface VisitActions {
  /** Какое действие сейчас выполняется. null — ни одного. */
  readonly running: VisitActionKind | null
  readonly problem: ApiProblem | null
  readonly arrive: (appointment: CabinetAppointmentView) => void
  readonly complete: (appointment: CabinetAppointmentView) => void
  readonly noShow: (appointment: CabinetAppointmentView, comment: string) => void
  readonly dismissProblem: () => void
}

export function useVisitActions(onDone: () => void): VisitActions {
  const [running, setRunning] = useState<VisitActionKind | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // Книга живёт столько же, сколько экран: ключ обязан пережить повтор, но не обязан
  // переживать уход с экрана — вернувшись, человек принимает решение заново.
  const book = useMemo(() => createIdempotencyBook(), [])

  const run = useCallback(
    (
      kind: VisitActionKind,
      appointment: CabinetAppointmentView,
      call: (key: string) => Promise<unknown>,
    ) => {
      if (running !== null) return
      const intent = appointmentIntent(kind, appointment.id, appointment.updatedAt)
      setRunning(kind)
      setProblem(null)

      const send = async (): Promise<void> => {
        try {
          await call(book.keyFor(intent))
          // Намерение исполнено: следующее такое же действие — уже другое намерение.
          book.forget(intent)
          onDone()
        } catch (error) {
          setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
        } finally {
          setRunning(null)
        }
      }
      void send()
    },
    [book, onDone, running],
  )

  const arrive = useCallback(
    (appointment: CabinetAppointmentView) => {
      run("arrived", appointment, (key) =>
        markAppointmentArrived(
          { appointmentId: appointment.id, expectedUpdatedAt: appointment.updatedAt },
          key,
        ),
      )
    },
    [run],
  )

  const complete = useCallback(
    (appointment: CabinetAppointmentView) => {
      run("completed", appointment, (key) =>
        markAppointmentCompleted(
          {
            appointmentId: appointment.id,
            expectedUpdatedAt: appointment.updatedAt,
            acknowledgedWarnings: [],
          },
          key,
        ),
      )
    },
    [run],
  )

  const noShow = useCallback(
    (appointment: CabinetAppointmentView, comment: string) => {
      const text = comment.trim()
      run("no-show", appointment, (key) =>
        markAppointmentNoShow(
          {
            appointmentId: appointment.id,
            expectedUpdatedAt: appointment.updatedAt,
            // Ограничение онлайн-записи мастер не ставит: блок blacklist требует права
            // clients.blacklist.manage, которого у него нет. Показать отметку и получить
            // отказ после нажатия — худший исход, чем не показать её вовсе.
            ...(text === "" ? {} : { comment: text }),
          },
          key,
        ),
      )
    },
    [run],
  )

  const dismissProblem = useCallback(() => {
    setProblem(null)
  }, [])

  return { running, problem, arrive, complete, noShow, dismissProblem }
}

export interface PhoneReveal {
  /** Открытый номер. null — ещё не открывали либо открыть не удалось. */
  readonly phone: string | null
  readonly running: boolean
  readonly problem: ApiProblem | null
  readonly request: (clientId: string, appointmentId: string) => void
}

/**
 * «Показать» полный номер клиента.
 *
 * ЭТО ДЕЙСТВИЕ, А НЕ ПОЛЕ, и потому у него отдельный запрос: каждое открытие пишется
 * в аудит, и салон получает отчёт «кто сколько контактов открыл за неделю». Это
 * единственный работающий инструмент против тихого выноса базы глазами (GAPS E3).
 *
 * ИДЕНТИФИКАТОР ЗАПИСИ ПЕРЕДАЁТСЯ ВСЕГДА. В аудите он отличает работу от перебора базы:
 * открыл номер из своего визита — понятно зачем; открыл без записи и без объяснения —
 * это и есть предмет разбора.
 */
export function useRevealPhone(): PhoneReveal {
  const [phone, setPhone] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  const request = useCallback(
    (clientId: string, appointmentId: string) => {
      if (running) return
      setRunning(true)
      setProblem(null)

      const send = async (): Promise<void> => {
        try {
          const result = await revealClientPhone({ clientId, appointmentId })
          setPhone(result.phone)
        } catch (error) {
          setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
        } finally {
          setRunning(false)
        }
      }
      void send()
    },
    [running],
  )

  return { phone, running, problem, request }
}
